import { Col, Divider, Row, Typography } from 'antd';
import { CfSimCompareInfo } from 'domain/entities/SimulateEntity';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import { commonHelpers } from 'utilities/helpers';

export const ItemComponent = ({
    value,
    type = 'number',
    suffix,
}: {
    value?: any;
    type?: string;
    suffix?: string;
}) => {
    const displayValue = (value: any) => {
        if (!value && value !== 0) return '-';
        if (type == 'number' && !isNaN(value)) {
            if (suffix === '%') {
                return Number(value).toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                });
            }
            return commonHelpers.formatMoney(Number(value), 1);
        }
        return value;
    };

    return (
        <>
            <Typography.Text className="text-right">
                <span className="sim-value">{`${displayValue(value)} `}</span>
                {suffix && <span className="sim-suffix">{suffix}</span>}
            </Typography.Text>
        </>
    );
};

const BADGE_NAME = {
    100: '購入申込時',
    110: '確定',
};
export const StatusBadge = ({ type }: { type?: number }) => {
    return (
        <>
            {(type == 100 || type == 110) && (
                <div className={`badge py-3 px-2 w-fit badge-${type}`}>{BADGE_NAME[type]}</div>
            )}
        </>
    );
};

const TITLE_COL_SPAN = 4;
const CONTENT_COL_SPAN = 8;

export const SimDetailComponent = ({ data }: { data?: CfSimCompareInfo }) => {
    return (
        <>
            <Row className="items-start w-full" gutter={[0, 12]}>
                <Row className="w-full sim-title-row" gutter={[0, 12]}>
                    <Col span={18} className="w-full sim-compare-col">
                        <Typography.Text className="title">{data?.simulationTitle}</Typography.Text>
                    </Col>
                    <Col span={5} className="w-full sim-compare-col content">
                        {data?.simulationTiming
                            ? commonHelpers.formatDate(
                                  data?.simulationTiming,
                                  DATE_FORMAT.FORMAT_DATE_WITH_DAY,
                              )
                            : null}
                    </Col>
                </Row>

                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        毎月の収支
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.monthlyBalanceOfPayments} suffix="円" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        年間の収支
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.yearlyBalanceOfPayments} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        ローン金額
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.loanAmount} suffix="円" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        賃貸管理手数料
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.managementOutsourcingFee} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        月々のお支払い
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.monthlyPaymentAmount} suffix="円" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        管理費等
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.totalManagementFee} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        家賃収入
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.rentalIncome} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        家賃収入
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.rentalIncome} suffix="円" />
                    </Col>
                </Row>

                <Divider />

                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={24} className="w-full sim-compare-col title title-bold">
                        入力条件
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        購入価格
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col small content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent
                            value={data?.purchasePriceIncludingBrokerageFee}
                            suffix="円"
                        />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col small title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        自己資金
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.personalFunds*100000} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        返済期間
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col small content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.repaymentPeriod} suffix="年" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col small title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        仲介手数料
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent
                            value={data?.brokerageFee}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        賃料
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col small content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.rent} suffix="円" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col small title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        共益費
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.commonServiceFee} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        管理費
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col small content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.managementFee} suffix="円" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col small title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        修繕積立金
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.reserveFundForRepairs} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row">
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        金融機関
                    </Col>
                    <Col
                        span={CONTENT_COL_SPAN}
                        className="w-full sim-compare-col small content pr-[10px] xl:pr-[20px] 2xl:pr-[40px]"
                    >
                        <ItemComponent value={data?.financialInstitutionNameDisp} type="text" />
                    </Col>
                    <Col
                        span={TITLE_COL_SPAN}
                        className="w-full sim-compare-col small title pl-10px xl:pl-[20px] 2xl:pl-[40px]"
                    >
                        金利
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.interestRate} suffix="%" />
                    </Col>
                </Row>
            </Row>
        </>
    );
};
